<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :data="decoratedItems"
      :autoLoad="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @refresh="loadData"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOLaudosComparison } from '../../composables/useSOLaudosComparison';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useSOStatusMap } from '../../composables/useSOStatusMap';

const { t } = useI18n();
const { isLoading, items, total, fetchComparisonItems } = useSOLaudosComparison();
const { formatDateTime, formatDate } = useDateFormatter();
const { getOrderStatus, orderStatusMap } = useSOStatusMap();

const dataTableRef = ref<any>(null);

const loadData = async (): Promise<void> => {
  log('SOLaudosAnalysisTable', 'loadData', 'Loading comparison items');
  try {
    await fetchComparisonItems();
    eventBus.emit('globalMessage.success', `${items.value.length} ${t('registros de análise de laudos carregados')}`);
  } catch (err: any) {
    logError('SOLaudosAnalysisTable', 'loadData', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar análise de laudos: ') + err.message);
  }
};

const decoratedItems = computed(() =>
  items.value.map((r: Record<string, unknown>) => ({
    ...r,
    _so_order_label: `Ordem ID: ${r.so_order_id}`,
    _so_order_item_label: `Item ID: ${r.so_order_item_id}`,
    _service_with_priority: r.so_priority_name
      ? `${r.so_service_name ?? ''} ${r.so_priority_name}`.trim()
      : (r.so_service_name ?? ''),
  }))
);

const tableColumns = computed(() => [
  {
    data: '_so_order_label',
    sortField: 'so_order_id',
    title: t('OS'),
    width: 110,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
      const orderId = row?.so_order_id;
      if (orderId !== undefined) {
        eventBus.emit('modal.pdv-checkout.open', { orderId });
      }
    },
    mergedColumns: [
      {
        data: '_so_order_item_label',
        title: t('Item'),
        renderType: 'custom_clickable_id',
        clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
          const itemId = row?.so_order_item_id;
          if (itemId) {
            eventBus.emit('modal.so-order-item.open', { itemId });
          }
        },
      },
    ],
  },
  {
    data: null,
    title: t('Comparar'),
    width: 120,
    renderType: 'clickable_button',
    buttonText: t('Comparar'),
    buttonIcon: 'pi pi-eye',
    buttonClass: 'p-button-sm p-button-outlined btn-comparar'
  },
  {
    data: 'so_order_client_name',
    title: t('Cliente'),
    width: 220,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
      const clienteId = row?.so_order_client_id;
      if (clienteId) {
        eventBus.emit('modal.cliente.open', { id: clienteId });
      }
    },
    mergedColumns: [
      { data: 'franchise_name', title: t('Franquia') }
    ]
  },
  {
    data: 'so_order_item_qrcode',
    title: t('QR Code'),
    width: 120,
    sortable: true
  },
  {
    data: '_service_with_priority',
    title: t('Serviço'),
    width: 220,
    sortable: true,
    filterable: true,
    filterType: 'text',
    mergedColumns: [
      { data: 'so_service_category_name', title: t('Categoria') },
      { data: 'so_order_item_description', title: t('Descrição') },
    ],
  },
  {
    data: 'so_order_status',
    title: t('Status'),
    width: 120,
    sortable: true,
    filterType: 'select',
    filterOptions: Object.entries(orderStatusMap as Record<string, { label: string }>).map(([value, { label }]) => ({ value, label })),
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>): { label: string; severity: string } => {
      const mapped = getOrderStatus(row.so_order_status);
      return { label: mapped.label, severity: mapped.severity };
    },
  },
  {
    data: 'entry_report_photo',
    title: t('Foto Entrada'),
    width: 70,
    renderType: 'image',
    imageConfig: { width: 48, height: 48 }
  },
  {
    data: 'entry_report_confirmed_at',
    title: t('Laudo Entrada'),
    width: 140,
    sortable: true,
    renderType: 'datetime'
  },
  {
    data: 'exit_report_photo',
    title: t('Foto Saída'),
    width: 70,
    renderType: 'image',
    imageConfig: { width: 48, height: 48 }
  },
  {
    data: 'exit_report_confirmed_at',
    title: t('Laudo Saída'),
    width: 140,
    sortable: true,
    renderType: 'datetime'
  }
]);

const dataTableConfig = computed(() => ({
  tableId: 'tabelaAnaliseLaudos',
  filename: `analise_laudos_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.so_order_item_id;
    return id == null ? '' : `so-laudos-row-${id}`;
  },
  pageLength: 50,
  pagination: true,
  rowSelection: 'single',
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  columns: {
    'so_order_client_name': 'text',
    'franchise_name': 'select',
    'so_service_name': 'select',
    'so_order_status': 'select',
    'so_order_item_qrcode': 'qrcode'
  }
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV'),
  loading: false
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
  loading: isLoading.value
}));

const handleButtonClick = (payload: any): void => {
  const row = payload?.data;
  if (row?.so_order_item_id) {
    log('SOLaudosAnalysisTable', 'handleButtonClick', 'Opening comparison modal', row);
    eventBus.emit('modal.laudos-comparison.open', { orderItemId: row.so_order_item_id });
  }
};

onMounted(() => {
  log('SOLaudosAnalysisTable', 'mounted', 'Component mounted');
  // No filter.updated listener — loadData() in onMounted covers the initial boot,
  // and the DataTable's built-in refresh button handles manual reloads.
  // This avoids the double-load race when filter.updated fires at boot time
  // (useFiltro.ts:227) concurrently with the onMounted loadData().
  loadData();
});

onBeforeUnmount(() => {
  log('SOLaudosAnalysisTable', 'beforeUnmount', 'Cleaning up');
});

defineExpose({ loadData });
</script>

<style scoped>
</style>
